import { useMemo, useState } from "react";
import type { Category } from "@/types";
import type { CatalogRowStatus, ExtractedImage, PdfCatalogRow } from "@/utils/pdfCatalog";
import ImageAssignModal from "@/pages/admin/respaldo/components/ImageAssignModal";

const CATEGORIES: Category[] = ["Aseo Personal", "Hogar", "Nutrición", "Belleza"];

const STATUS_META: Record<CatalogRowStatus, { label: string; classes: string }> = {
  nuevo: { label: "Nuevo", classes: "bg-accent-500/20 text-accent-200" },
  cambio: { label: "Cambio de precio", classes: "bg-secondary-500/25 text-secondary-100" },
  foto: { label: "Foto nueva", classes: "bg-primary-500/25 text-primary-100" },
  sin_cambios: { label: "Sin cambios", classes: "bg-background-300/40 text-foreground-500" },
};

interface PdfCatalogReviewTableProps {
  rows: PdfCatalogRow[];
  images: ExtractedImage[];
  onUpdate: (id: string, patch: Partial<PdfCatalogRow>) => void;
  onRemove: (id: string) => void;
}

const inputCls =
  "w-full bg-background-100/70 border border-background-300/50 rounded-md px-2 py-1.5 text-xs text-foreground-950 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/20";

const PdfCatalogReviewTable = ({
  rows,
  images,
  onUpdate,
  onRemove,
}: PdfCatalogReviewTableProps) => {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"todos" | CatalogRowStatus>("todos");
  const [photoFor, setPhotoFor] = useState<string | null>(null);

  const counts = useMemo(() => {
    const base: Record<CatalogRowStatus, number> = {
      nuevo: 0,
      cambio: 0,
      foto: 0,
      sin_cambios: 0,
    };
    rows.forEach((r) => {
      base[r.status] += 1;
    });
    return base;
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const matchStatus = statusFilter === "todos" || r.status === statusFilter;
      const matchQuery = !q || r.name.toLowerCase().includes(q) || r.sku.toLowerCase().includes(q);
      return matchStatus && matchQuery;
    });
  }, [rows, query, statusFilter]);

  const activeRow = rows.find((r) => r.id === photoFor) ?? null;

  return (
    <div className="animate-fade-up">
      {/* Resumen por estado */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {(
          [
            { key: "nuevo", icon: "ri-add-circle-line" },
            { key: "cambio", icon: "ri-price-tag-3-line" },
            { key: "foto", icon: "ri-image-2-line" },
            { key: "sin_cambios", icon: "ri-checkbox-circle-line" },
          ] as const
        ).map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(statusFilter === s.key ? "todos" : s.key)}
            className={`rounded-md border px-3 py-2 text-left cursor-pointer transition-colors ${
              statusFilter === s.key
                ? "border-accent-500/50 bg-accent-500/10"
                : "border-background-300/40 bg-background-200/40 hover:border-accent-500/30"
            }`}
          >
            <p className="text-[10px] text-foreground-500 flex items-center gap-1">
              <i className={s.icon} /> {STATUS_META[s.key].label}
            </p>
            <p className="text-sm font-bold text-foreground-950">{counts[s.key]}</p>
          </button>
        ))}
      </div>

      <div className="relative max-w-sm mb-3">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
          <i className="ri-search-line" />
        </span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre o SKU..."
          className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50"
          aria-label="Buscar en la vista previa"
        />
      </div>

      <div className="glass-panel rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[520px]">
          <table className="w-full min-w-[1000px] text-left">
            <thead className="sticky top-0 z-10 bg-background-100/95 backdrop-blur">
              <tr className="border-b border-accent-500/20 text-[11px] uppercase tracking-wide text-foreground-500">
                <th className="px-3 py-3 font-semibold">Foto</th>
                <th className="px-3 py-3 font-semibold">SKU</th>
                <th className="px-3 py-3 font-semibold">Nombre del producto</th>
                <th className="px-3 py-3 font-semibold text-right">Precio público</th>
                <th className="px-3 py-3 font-semibold text-right">Puntos PV</th>
                <th className="px-3 py-3 font-semibold">Categoría</th>
                <th className="px-3 py-3 font-semibold">Estado</th>
                <th className="px-3 py-3 font-semibold text-right">Acción</th>
              </tr>
            </thead>
            <tbody className="text-sm">
              {filtered.map((r) => {
                const meta = STATUS_META[r.status];
                return (
                  <tr
                    key={r.id}
                    className="border-b border-background-300/25 hover:bg-background-200/30 transition-colors align-top"
                  >
                    <td className="px-3 py-2">
                      <button
                        onClick={() => setPhotoFor(r.id)}
                        className="w-14 h-14 rounded-md border border-background-300/50 bg-background-200/40 flex items-center justify-center overflow-hidden cursor-pointer hover:border-accent-500/50"
                        title="Cambiar foto"
                      >
                        {r.imageUrl ? (
                          <img src={r.imageUrl} alt={r.name} className="w-full h-full object-contain" />
                        ) : (
                          <i className="ri-image-add-line text-accent-300 text-lg" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        value={r.sku}
                        onChange={(e) => onUpdate(r.id, { sku: e.target.value })}
                        className={`${inputCls} font-mono text-accent-200`}
                        aria-label="SKU"
                      />
                    </td>
                    <td className="px-3 py-2 min-w-[240px]">
                      <input
                        value={r.name}
                        onChange={(e) => onUpdate(r.id, { name: e.target.value })}
                        className={inputCls}
                        aria-label="Nombre del producto"
                      />
                      {r.presentation && (
                        <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-background-300/40 text-foreground-600">
                          {r.presentation}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={r.pricePublic || ""}
                        onChange={(e) => onUpdate(r.id, { pricePublic: Number(e.target.value) || 0 })}
                        className={`${inputCls} text-right`}
                        aria-label="Precio público"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={r.pv || ""}
                        placeholder="0"
                        onChange={(e) => onUpdate(r.id, { pv: Number(e.target.value) || 0 })}
                        className={`${inputCls} text-right`}
                        aria-label="Puntos PV"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={r.category}
                        onChange={(e) => onUpdate(r.id, { category: e.target.value as Category })}
                        className={`${inputCls} cursor-pointer`}
                        aria-label="Categoría"
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block text-[10px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${meta.classes}`}
                      >
                        {meta.label}
                      </span>
                      <span className="block text-[10px] text-foreground-500 mt-1">pág. {r.page}</span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => onRemove(r.id)}
                        className="w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground-600 hover:text-primary-200 hover:bg-background-300/40 cursor-pointer"
                        aria-label="Quitar de la importación"
                        title="Quitar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <p className="px-4 py-6 text-center text-xs text-foreground-500">
            No hay filas que coincidan con el filtro.
          </p>
        )}
      </div>
      <p className="text-[10px] text-foreground-500 mt-2 flex items-center gap-1.5">
        <i className="ri-information-line" /> Todo es editable: corrige lo que la lectura automática
        haya interpretado mal antes de importar.
      </p>

      <ImageAssignModal
        open={photoFor !== null}
        images={images}
        currentUrl={activeRow?.imageUrl ?? ""}
        onPick={(dataUrl) => {
          if (photoFor) onUpdate(photoFor, { imageUrl: dataUrl });
        }}
        onClose={() => setPhotoFor(null)}
      />
    </div>
  );
};

export default PdfCatalogReviewTable;